import { useTranslation } from 'react-i18next'
import { AppIcon } from './ui/icon'
import { mdiShieldAccount, mdiShieldAlert, mdiShieldCheck, mdiShieldOutline } from '@mdi/js'
import { Badge } from './ui/badge'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { piiStateOf, type PIIState, type PIIStatus } from '../lib/pii'

/**
 * Tri-state PII badge (spec 2026-08-13-02 §8) — shared by the document and the
 * contract detail pages, which render the exact same four states off the exact
 * same `flags` array.
 *
 * The four states and their intent:
 *   - `confirmed` — a human said "yes" (destructive, it is the loudest);
 *   - `presumed`  — inherited from the type, nobody looked yet (warning);
 *   - `none`      — a human said "no" (success);
 *   - `unassessed`— nobody looked and the type carries no presumption (outline).
 *
 * When `onSetStatus` is provided the badge becomes a dropdown offering the
 * three verdicts, wired to the `pii_status` PATCH field. Without it, it renders
 * as a plain read-only badge (list rows).
 */

const STATE_STYLE: Record<PIIState, { variant: 'destructive' | 'warning' | 'success' | 'outline'; icon: string }> = {
    confirmed: { variant: 'destructive', icon: mdiShieldAlert },
    presumed: { variant: 'warning', icon: mdiShieldAccount },
    none: { variant: 'success', icon: mdiShieldCheck },
    unassessed: { variant: 'outline', icon: mdiShieldOutline },
}

interface PIIBadgeProps {
    flags?: string[] | null
    /** When set, the badge opens a verdict menu. Omit for a read-only badge. */
    onSetStatus?: (status: PIIStatus) => void
    /** Hide the badge entirely when nothing has been assessed (list rows). */
    hideWhenUnassessed?: boolean
    disabled?: boolean
    testId?: string
    className?: string
}

export function PIIBadge({
    flags,
    onSetStatus,
    hideWhenUnassessed = false,
    disabled = false,
    testId = 'pii-badge',
    className,
}: PIIBadgeProps) {
    const { t } = useTranslation('common')
    const state = piiStateOf(flags)

    if (hideWhenUnassessed && state === 'unassessed') return null

    const { variant, icon } = STATE_STYLE[state]
    const badge = (
        <Badge
            variant={variant}
            className={`text-xs gap-1 ${onSetStatus && !disabled ? 'cursor-pointer' : ''} ${className || ''}`}
            title={t(`pii.state_hint.${state}`)}
            data-testid={testId}
            data-pii-state={state}
        >
            <AppIcon path={icon} size="xs" />
            {t(`pii.state.${state}`)}
        </Badge>
    )

    if (!onSetStatus || disabled) return badge

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild data-testid={`${testId}-trigger`}>
                {badge}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
                <DropdownMenuItem
                    onClick={() => onSetStatus('confirmed')}
                    data-testid={`${testId}-set-confirmed`}
                >
                    <AppIcon path={mdiShieldAlert} size="sm" className="mr-2" />
                    {t('pii.actions.confirm')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onSetStatus('none')} data-testid={`${testId}-set-none`}>
                    <AppIcon path={mdiShieldCheck} size="sm" className="mr-2" />
                    {t('pii.actions.none')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onSetStatus('clear')} data-testid={`${testId}-set-clear`}>
                    <AppIcon path={mdiShieldOutline} size="sm" className="mr-2" />
                    {t('pii.actions.clear')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export default PIIBadge
